import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Component, useState, type ReactNode } from "react";
import { DOC_TYPES, data, portal, type ClientOrg, type Item } from "@aawaz/api-client";
import { t } from "@aawaz/i18n";
import { Badge, Button, ErrorBanner, Field, Icon, Input, KV, Loading, MonthPicker, Notice, Select, fmtBytes, fmtDate, fmtPeriod, humanize, sourceLabel } from "@aawaz/ui";
import { displayName } from "../pages/Documents";
import { useSession } from "../session";

export type DocType = (typeof DOC_TYPES)[number];
export type Action = { action: "accept" | "quarantine" | "reclassify" | "set_client" | "unlock"; reason?: string;
  doc_type?: DocType; client_org_id?: string; period?: string; password?: string };

export function useAct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, a }: { id: string; a: Action }) =>
      data<Item>(portal.POST("/portal/v1/items/{item_id}:act", { params: { path: { item_id: id } }, body: a })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["items"] }),
  });
}

export function useClients() {
  return useQuery({ queryKey: ["clients"], queryFn: () => data<{ data: ClientOrg[] }>(portal.GET("/portal/v1/clients")) });
}

/** Document preview (sandboxed, 5-min signed URL, every view audited) + triage record + review actions. */
/** Why a document is waiting, and what to do about it. */
const REASON_HELP: Record<string, string> = {
  UNKNOWN_SENDER: "Sent from a number that is not linked to any client. Choose the client; to stop this, add the number to that client.",
  CLIENT_UNRESOLVED: "Sent by staff, and no client could be worked out from the bill. Choose the client.",
  UNCERTAIN_RELEVANCE: "It was not clear that this is a business document. Accept it if it is, or hold it back if it is not.",
  LOW_QUALITY: "Too blurred or dark to read reliably. Accept it as it is, or ask the sender for a clearer copy.",
  PASSWORD_REQUIRED: "The PDF is password-protected. Enter the password below; it is used once and not stored.",
  NOT_FINANCIAL: "Looks like a personal or non-business file. Accept it only if it belongs in the client's records.",
  POSSIBLE_DUPLICATE: "Looks like a document already received. Compare with the one named under Details, then accept or hold back.",
  EXACT: "The same file was already received; this copy is not filed twice.",
  NEAR: "A near-identical copy of a document already received.",
  EMPTY: "The file is empty. Ask the sender to send it again.",
  TOO_LARGE: "The file is larger than the limit. Ask for a smaller or split file.",
  UNSAFE_ARCHIVE: "A zip that could not be opened safely. Ask for the files themselves.",
  SPOOF_SUSPECTED: "The email may not be from who it says. Check with the sender before accepting.",
  LLM_BUDGET_EXHAUSTED: "Automatic sorting was paused for today, so this one needs a person.",
  CORRUPT: "The file is damaged and cannot be opened. Ask the sender to send it again.",
  UNSUPPORTED_TYPE: "This kind of file cannot be read. Ask for a PDF or a photo.",
  MALWARE: "The virus scan flagged this file. It cannot be opened or accepted.",
  FETCH_EXHAUSTED: "The file could not be downloaded from the sender's channel. It is retried for three days.",
  RETENTION_EXPIRED: "Removed after the firm's retention period.",
  STATEMENT_NOT_PROVEN: "A running balance on this bank statement does not follow from the line before, or its opening or closing does not match. The rows are named in the reasons. Ask for the statement again from net banking.",
  UNKNOWN_LAYOUT: "This looks like a bank statement or a payout statement in a layout not known yet. Nothing was read from it. The columns found are in the reasons. Send a copy to AawazAI support so the layout can be added.",
};

export function ItemPanel({ id, onDone }: { id: string; onDone?: () => void }) {
  const s = useSession();
  const item = useQuery({ queryKey: ["items", "one", id], queryFn: () =>
    data<Item>(portal.GET("/portal/v1/items/{item_id}", { params: { path: { item_id: id } } })) });
  const clients = useClients();
  const act = useAct();
  const [password, setPassword] = useState("");
  if (item.isLoading) return <Loading />;
  const it = item.data;
  if (!it) return <ErrorBanner error={item.error} />;
  const blocked = it.reason_code === "MALWARE" || it.status === "PURGED" || !it.content_url;
  const src = `/portal/v1/items/${it.id}/content?inline=1`;          // preview; the download link stays an attachment
  const run = async (a: Action) => { await act.mutateAsync({ id: it.id, a }); onDone?.(); };
  const clientName = clients.data?.data.find((c) => c.id === it.client_org_id)?.display_name;
  const editable = ["ACCEPTED", "NEEDS_REVIEW", "NEEDS_INPUT"].includes(it.status);
  // a practice files every document under a client
  const needsClient = s.has("ca") && !it.client_org_id && ["QUARANTINED", "NEEDS_REVIEW"].includes(it.status);
  return (
    <div className="ui-doc">
      <div className="ui-doc-preview">
        {s.can("download") && !blocked ? (
          it.mime?.startsWith("image/")
            ? <img src={src} alt={it.filename ?? "document"} />
            : it.mime === "application/pdf"
              // no sandbox attribute: browsers refuse to run their PDF viewer in a sandboxed frame; the file is a
              // signed PDF served with nosniff, and only PDFs and images are ever served inline
              ? <iframe title="Document preview" className="ui-preview" src={src} />
              : <div className="ui-empty"><a className="ui-btn" href={src} rel="noreferrer"><Icon name="download" size={16} />Download {it.filename}</a></div>
        ) : <p className="ui-empty">{s.can("download") ? t("doc.preview_blocked")
          : "Your role can see what arrived but not open documents. Ask an owner or admin of the firm for access."}</p>}
      </div>
      <div className="ui-doc-side">
        <ErrorBanner error={act.error} />
        <div style={{ display: "grid", gap: 8 }}>
          <div className="ui-row"><Badge value={it.status} />{it.reason_code ? <Badge value={it.reason_code} tone="grey" /> : null}</div>
          <h2 style={{ fontSize: 18, overflowWrap: "anywhere" }}>{displayName(it)}</h2>
          <span className="ui-hint">{clientName ?? (it.client_org_id ? "…" : "Unassigned")} · {sourceLabel(it.source)}
            {it.sender?.name ? ` · ${it.sender.name}` : ""}</span>
        </div>
        {it.reason_code && REASON_HELP[it.reason_code] && it.status !== "ACCEPTED" ? <p className="ui-hint" style={{ margin: 0 }}>{REASON_HELP[it.reason_code]}</p> : null}
        {needsClient ? <p className="ui-hint" style={{ margin: 0 }}>Choose the client under “Correct it” first; a document without a client is not filed anywhere.</p> : null}
        {s.can("review") && it.status !== "PURGED" && it.reason_code !== "MALWARE" ? (
          <div className="ui-row">
            {["QUARANTINED", "NEEDS_REVIEW"].includes(it.status) ?
              <Button variant="primary" busy={act.isPending} disabled={needsClient} onClick={() => void run({ action: "accept" })}><Icon name="check" size={16} />{t("review.accept")}</Button> : null}
            {editable ?
              <Button variant="danger" busy={act.isPending} onClick={() => void run({ action: "quarantine", reason: "marked invalid" })}>{t("review.quarantine")}</Button> : null}
          </div>
        ) : null}
        {it.reason_code === "PASSWORD_REQUIRED" && s.can("review") ? (
          <form className="ui-doc-section" onSubmit={(e) => { e.preventDefault(); void run({ action: "unlock", password }); setPassword(""); }}>
            <h3>Unlock</h3>
            <div className="ui-row" style={{ flexWrap: "nowrap" }}>
              <Input type="password" autoComplete="off" placeholder="PDF password (used once, never stored)"
                     value={password} onChange={(e) => setPassword(e.target.value)} />
              <Button type="submit" busy={act.isPending}><Icon name="lock" size={16} />{t("review.unlock")}</Button>
            </div>
          </form>
        ) : null}
        <section className="ui-doc-section">
          <h3>Details</h3>
          <KV items={[
            ["Type", it.doc_type ? `${humanize(it.doc_type)} · ${Math.round((it.doc_type_confidence ?? 0) * 100)}% sure` : null],
            ["Period", it.period ? `${fmtPeriod(it.period)} · FY ${it.fy}` : null],
            ["Received", fmtDate(it.received_at)],
            ["Size", fmtBytes(it.size_bytes)],
            ["Doc no / GSTIN", [it.hints?.doc_no, ...(it.hints?.gstins ?? [])].filter(Boolean).join(" · ") || null],
            ["Caption", (it.context?.caption as string) ?? null],
            ["Duplicate of", it.duplicate_of],
            ["Earlier version", it.previous_version_id],
            ["Stored in AawazAI at", it.vault_path ? <span className="ui-mono">{it.vault_path}</span> : null],
            ["Drive copy", it.drive_filing ? (it.drive_filing.reason ?? (it.drive_filing.state === "filed"
              ? <span className="ui-mono">{it.drive_filing.path}</span> : "Being copied to the drive")) : null],
          ].filter(([, v]) => v != null && v !== "—") as [string, ReactNode][]} />
        </section>
        <ReadBoundary key={it.id}><BillRead itemId={it.id} canFix={s.can("review") && editable} /></ReadBoundary>
        {it.triage ? (
          <section className="ui-doc-section">
            <h3>Why this decision</h3>
            <details>
              <summary>{humanize(it.triage.decision)} · {Math.round(it.triage.confidence * 100)}% confidence</summary>
              <ul style={{ margin: "8px 0", paddingLeft: 18 }}>{it.triage.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
              <small className="ui-hint">Policy {it.triage.policy_version} · {Object.entries(it.triage.models ?? {}).map(([k, v]) => `${k}: ${v}`).join(", ")}</small>
            </details>
          </section>
        ) : null}
        {s.can("review") && it.status !== "PURGED" && it.reason_code !== "MALWARE" && editable ? (
          <section className="ui-doc-section">
            <h3>Correct it</h3>
            <div className="ui-grid">
              <Field label={t("review.set_client")}>{(fid) => (
                <Select id={fid} value={it.client_org_id ?? ""} onChange={(e) => void run({ action: "set_client", client_org_id: e.target.value })}
                        options={[{ value: "", label: "—" }, ...(clients.data?.data ?? []).map((c) => ({ value: c.id, label: c.display_name }))]} />)}
              </Field>
              <Field label={t("review.set_type")}>{(fid) => (
                <Select id={fid} value={it.doc_type ?? ""} onChange={(e) => void run({ action: "reclassify", doc_type: e.target.value as DocType })}
                        options={[{ value: "", label: "—" }, ...DOC_TYPES.map((d) => ({ value: d, label: humanize(d) }))]} />)}
              </Field>
              <Field label={t("review.set_period")}>{(fid) => (
                <MonthPicker id={fid} value={it.period ?? ""} anyLabel="—" onChange={(v) => v && v !== it.period
                  && void run({ action: "reclassify", period: v })} />)}
              </Field>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

const SUMMARY_FIELDS: [string, string][] = [
  ["supplier_name", "Supplier"], ["supplier_gstin", "Supplier GSTIN"], ["buyer_name", "Buyer"], ["buyer_gstin", "Buyer GSTIN"],
  ["invoice_number", "Invoice no"], ["invoice_date", "Invoice date"], ["taxable_total", "Taxable value"], ["tax_total", "Tax"],
  ["total", "Total"], ["irn", "IRN"], ["eway_bill", "E-way bill"],
];
const SUMMARY_LABEL: Record<string, string> = Object.fromEntries(SUMMARY_FIELDS);
interface ReadIssue { code: string; severity: string; field?: string | null; detail?: string | null }
interface ReadDoc {
  index: number; category: string; status: string; confidence: number; handwritten?: boolean | null; direction: string;
  irn_verified: boolean; summary: Record<string, string | null>; issues: ReadIssue[];
  gstin_repairs: { side: string; read: string; repaired_to: string }[];
  duplicate_of?: { item_id: string; index: number; received_at: string } | null;
  corrections: { field: string; before: string | null; after: string | null; at: string }[];
}
interface BillReadOut { read: { extraction_id: string; read_at: string; outcome: string; engine: string; documents: ReadDoc[] } | null }
const DIRECTION: Record<string, string> = { inward: "Purchase", outward: "Sale" };

/** What the reader saw on the bill: fields, checks that failed, copies of earlier bills, and fixes people made. */
function BillRead({ itemId, canFix }: { itemId: string; canFix: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["items", "read", itemId], queryFn: () =>
    data<BillReadOut>(portal.GET("/portal/v1/ca/items/{item_id}/read", { params: { path: { item_id: itemId } } })) });
  const again = useMutation({
    mutationFn: () => data(portal.POST("/portal/v1/ca/items/{item_id}/read:again", { params: { path: { item_id: itemId } } })),
    // the read takes a few seconds; look again shortly
    onSuccess: () => { setTimeout(() => void qc.invalidateQueries({ queryKey: ["items", "read", itemId] }), 8000); },
  });
  const read = q.data?.read;
  if (q.isLoading || !read) return null;
  const readAgain = canFix ? (
    <Button size="sm" variant="ghost" icon="refresh" busy={again.isPending} disabled={again.isSuccess} onClick={() => again.mutate()}
            title="Read this document afresh. Corrections you made are kept.">{again.isSuccess ? "Reading again…" : "Read again"}</Button>
  ) : null;
  if (read.outcome !== "read") {
    return <section className="ui-doc-section"><h3>What was read</h3>
      <ErrorBanner error={again.error} />
      <p className="ui-hint">{read.outcome === "retake" ? "The photo is too unclear to read; ask for a clearer one."
        : read.outcome === "rejected" ? "This does not look like a bill." : "The reader could not read this document."}</p>
      {readAgain}</section>;
  }
  return (
    <section className="ui-doc-section">
      <h3>What was read</h3>
      {read.documents.map((d) => <ReadDocView key={d.index} itemId={itemId} doc={d} many={read.documents.length > 1} canFix={canFix}
        onSaved={() => { void qc.invalidateQueries({ queryKey: ["items", "read", itemId] }); void qc.invalidateQueries({ queryKey: ["items", "one", itemId] }); }} />)}
      <ErrorBanner error={again.error} />
      <div className="ui-row" style={{ gap: 8 }}><small className="ui-hint">Read {fmtDate(read.read_at)} · {read.engine}</small>{readAgain}</div>
    </section>
  );
}

/** A read the panel cannot show must never take the document view (or the review queue) down with it. */
class ReadBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(e: unknown) { console.error("read panel failed", e); }
  render() {
    return this.state.failed
      ? <section className="ui-doc-section"><h3>What was read</h3><p className="ui-hint">This read could not be shown.</p></section>
      : this.props.children;
  }
}

function ReadDocView({ itemId, doc: raw, many, canFix, onSaved }: { itemId: string; doc: ReadDoc; many: boolean; canFix: boolean; onSaved: () => void }) {
  // reads stored by older versions may lack newer lists
  const doc: ReadDoc = { ...raw, summary: raw.summary ?? {}, issues: raw.issues ?? [], gstin_repairs: raw.gstin_repairs ?? [],
    corrections: raw.corrections ?? [] };
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: async () => {
      const changes = Object.fromEntries(Object.entries(draft).filter(([k, v]) => v !== (doc.summary[k] ?? "")));
      if (!Object.keys(changes).length) return null;                     // nothing changed: just close the form
      return data(portal.POST("/portal/v1/ca/items/{item_id}/read:correct", { params: { path: { item_id: itemId } },
        body: { document_index: doc.index, changes, reason: "corrected in the portal" } }));
    },
    onSuccess: () => { setEditing(false); onSaved(); },
  });
  const fixed = new Set(doc.corrections.map((c) => c.field));
  const startEdit = () => { setDraft(Object.fromEntries(SUMMARY_FIELDS.map(([k]) => [k, doc.summary[k] ?? ""]))); setEditing(true); };
  return (
    <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
      <div className="ui-row">
        {many ? <strong>Bill {doc.index + 1}</strong> : null}
        <Badge value={doc.status} />
        {DIRECTION[doc.direction] ? <Badge value={DIRECTION[doc.direction]!} tone="grey" /> : null}
        {doc.handwritten ? <Badge value="handwritten" tone="amber" /> : null}
        {doc.irn_verified ? <Badge value="e-invoice verified" tone="green" /> : null}
        <span className="ui-hint">{Math.round(doc.confidence * 100)}% sure</span>
      </div>
      {doc.duplicate_of ? <Notice>Copy of a bill received {fmtDate(doc.duplicate_of.received_at)}: it is not counted twice.</Notice> : null}
      {editing ? (
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} style={{ display: "grid", gap: 8 }}>
          <ErrorBanner error={save.error} />
          <div className="ui-grid">
            {SUMMARY_FIELDS.map(([k, label]) => (
              <Field key={k} label={label}>{(fid) => <Input id={fid} value={draft[k] ?? ""} placeholder={k === "invoice_date" ? "DD/MM/YYYY" : undefined}
                onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />}</Field>
            ))}
          </div>
          <div className="ui-row">
            <Button variant="primary" type="submit" busy={save.isPending}>Save fixes</Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </form>
      ) : (
        <>
          <KV items={SUMMARY_FIELDS.filter(([k]) => doc.summary[k]).map(([k, label]) =>
            [label, <span key={k}>{doc.summary[k]}{fixed.has(k) ? <> <Badge value="fixed" tone="grey" /></> : null}</span>] as [string, ReactNode])} />
          {canFix ? <div><Button onClick={startEdit}>Fix fields</Button></div> : null}
        </>
      )}
      {doc.issues.length ? (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {doc.issues.map((i, n) => <li key={`${i.code}-${n}`} className={i.severity === "error" ? "ui-red" : undefined}>
            {humanize(i.code)}{i.field ? ` (${SUMMARY_LABEL[i.field] ?? humanize(i.field)})` : ""}
            {i.detail ? <span className="ui-hint"> · {i.detail}</span> : null}</li>)}
        </ul>
      ) : null}
      {doc.gstin_repairs.length ? <small className="ui-hint">{doc.gstin_repairs.map((r) =>
        `${humanize(r.side)} GSTIN read as ${r.read}, matched to the client's ${r.repaired_to}`).join("; ")}</small> : null}
      {doc.corrections.length ? (
        <details>
          <summary className="ui-hint">{doc.corrections.length} fix{doc.corrections.length > 1 ? "es" : ""} by people</summary>
          <ul style={{ margin: "4px 0", paddingLeft: 18 }}>{doc.corrections.map((c, n) => (
            <li key={n}><span className="ui-hint">{fmtDate(c.at)}</span> {humanize(c.field)}: {c.before ?? "—"} → {c.after ?? "—"}</li>))}</ul>
        </details>
      ) : null}
    </div>
  );
}
